<template>
  <BillingErrorGuard
    v-if="coreSeatCost || errored"
    :errored
    :loading="loading"
    class="seat-breakdown"
  >
    <div class="seat-breakdown">
      <h5>Seat breakdown</h5>
      <section data-testid="core-seat-count">
        <div>
          <h6>{{ coreSeatLimit }} x Full Access seats</h6>
          <p>{{ centsToDollars(coreSeatCost) }} per seat/{{ billingPeriod }}</p>
        </div>
        <p>{{ centsToDollars(totalCoreSeatCost) }}</p>
      </section>
      <section v-if="salesSeatLimit" data-testid="sales-seat-count">
        <div>
          <h6>{{ salesSeatLimit }} x Sales seats</h6>
          <p
            >{{ centsToDollars(salesSeatCost) }} per seat/{{ billingPeriod }}</p
          >
        </div>
        <p>{{ centsToDollars(totalSalesSeatCost) }}</p>
      </section>
      <section data-testid="total-cost">
        <h5>Total</h5>
        <p class="font-bold text-success-text">{{
          centsToDollars(totalCoreSeatCost + totalSalesSeatCost)
        }}</p>
      </section>
    </div>
  </BillingErrorGuard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import useBilling from '@/composables/useBilling';
import useSeats from '@/composables/useSeats';
import { BILLING_PLAN_ANNUAL, BILLING_PLAN_MONTHLY } from '@/constants/billing';
import { captureException } from '@/errors';
import { centsToDollars } from '@/utils';

import BillingErrorGuard from './BillingErrorGuard.vue';

const { billingPeriod } = defineProps<{
  billingPeriod?: string;
}>();

const { salesSeatLimit, coreSeatLimit } = useSeats();
const { fetchSeatDataV4, processCostDataV4 } = useBilling();

const loading = ref(true);
const errored = ref(false);

const chosenPeriod = computed(() =>
  billingPeriod === 'month' ? BILLING_PLAN_MONTHLY : BILLING_PLAN_ANNUAL,
);

const salesSeatCost = ref(0);
const totalSalesSeatCost = ref(0);

const coreSeatCost = ref(0);
const totalCoreSeatCost = ref(0);

async function fetchPricesFromChargify() {
  try {
    errored.value = false;
    const costData = await fetchSeatDataV4({
      newCoreSeats: coreSeatLimit.value,
      newSalesSeats: salesSeatLimit.value,
      period: chosenPeriod.value,
    });

    if (costData) {
      const processedData = processCostDataV4({
        costData,
        newCoreSeats: coreSeatLimit.value,
        newSalesSeats: salesSeatLimit.value,
        period: chosenPeriod.value,
      });

      coreSeatCost.value = processedData.costPerCoreSeat as number;
      totalCoreSeatCost.value = processedData.coreSeatsTotalCost;
      salesSeatCost.value = processedData.costPerSalesSeat;
      totalSalesSeatCost.value = processedData.salesSeatsTotalCost;
    }
  } catch (error) {
    errored.value = true;
    captureException(error);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await fetchPricesFromChargify();
  loading.value = false;
});
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.seat-breakdown {
  @apply flex flex-col;
}
h5 {
  @apply font-bold text-base text-neutral-text-strong;
}
div > p {
  @apply text-sm;
}
section {
  @apply border-b border-solid border-neutral-border py-16 flex items-center justify-between;
  &:last-child {
    @apply border-b-0;
  }
}
</style>
